import { EditorState, Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import styles from './FloatingMenuAnchorPlugin.module.scss'

export const FloatingMenuAnchorPluginKey = new PluginKey('FloatingMenuAnchorPlugin')

type FloatingMenuAnchorPluginState = {
  ds: DecorationSet
}

type FloatingMenuAnchorPluginMeta =
  | 'clear'
  | {
      slashPos: number
      rootPos: number
      matchedText: string
    }

const createFloatingAnchorDecoEl =
  (slashPos: number, rootPos: number, matchedText: string) => (view: EditorView, getPos: () => number) => {
    const slashCoords = view.coordsAtPos(slashPos) // get the coords of the `/` - pretty simple

    /**
     * This one is complicated. We want to find the coordinate of the "root", that is, the immediate child of
     * `.ProseMirror` that has the slash.
     * We can't simply `view.coordsAtPos(rootPos)` though, because sometimes that gives us the wrong value
     * (usually it will give the coords of `.ProseMirror` itself).
     *
     * Also, strangely, in `view.domAtPos(somePos)`, somePos needs to be the first position of the node, not the pos
     * before the node (like most other prosemirror methods)
     */
    const rootCoords = (
      view.domAtPos(view.state.doc.resolve(rootPos).before(1) + 1).node as HTMLElement
    ).getBoundingClientRect()

    const offset = { x: slashCoords.left - rootCoords.left, y: slashCoords.top - rootCoords.top }
    const caretHeight = slashCoords.bottom - slashCoords.top
    offset.y += caretHeight // caret height

    const distanceFromTopBar = 20

    const el = document.createElement('div')
    el.classList.add(styles.placeholder)
    el.classList.add(styles[view.state.doc.resolve(rootPos)?.nodeAfter?.type?.name ?? ''])
    el.style.transform = `translate(calc(var(--offsetX) + ${offset.x}px), calc(var(--offsetY) + ${offset.y}px))`
    el.style.top = `${distanceFromTopBar - offset.y}px`
    el.style.bottom = `${offset.y + caretHeight}px`
    const inner = document.createElement('div')
    inner.id = 'floating-menu-anchor'
    /**
     * This `width` has nothing to do with width, it just exploits a css gotcha: when you specify a margin as a % unit,
     * it is a % of the container's (not the element's) width (never the height, as one might expect).
     *
     * In this case it is used to position the menu so that it doesn't cover the text, and we need to know the caret
     * height in order to do that
     */
    inner.style.width = `${caretHeight}px`
    inner.classList.add(styles.inner)
    inner.dataset.matchedText = matchedText
    el.appendChild(inner)
    return el
  }

export const FloatingMenuAnchorPlugin = new Plugin<FloatingMenuAnchorPluginState, any>({
  key: FloatingMenuAnchorPluginKey,
  state: {
    init() {
      return { ds: DecorationSet.empty }
    },
    apply(tr, state) {
      const meta = tr.getMeta(FloatingMenuAnchorPluginKey) as FloatingMenuAnchorPluginMeta
      if (!meta) {
        return { ds: state.ds.map(tr.mapping, tr.doc) }
      }
      if (meta === 'clear') {
        return { ds: DecorationSet.empty }
      }
      const { slashPos, rootPos, matchedText } = meta
      if (slashPos !== undefined) {
        return {
          ds: DecorationSet.create(tr.doc, [
            Decoration.widget(rootPos, createFloatingAnchorDecoEl(slashPos, rootPos, matchedText), {
              stopEvent: (e) => true,
            }),
          ]),
        }
      }
      return state
    },
  },
  props: {
    decorations: (state: EditorState) => {
      const { ds } = FloatingMenuAnchorPluginKey.getState(state)
      return ds
    },
  },
})
